import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Input,
  inject,
} from '@angular/core';
import {
  LinkColor,
  LinkHoverColor,
  LinkWeight,
  QueryParams,
} from '@freelancer/ui/link';
import { FontColor, TextSize, TextTransform } from '@freelancer/ui/text';
import { LinkModule } from '../link/link.module';

@Component({
  standalone: true,
  imports: [LinkModule],
  selector: `fl-breadcrumbs-item`,
  template: `
    <fl-link
      [color]="LinkColor.INHERIT"
      [hoverColor]="LinkHoverColor.INHERIT"
      [itemprop]="'item'"
      [itemtype]="'https://schema.org/Thing'"
      [link]="link"
      [queryParams]="queryParams"
      [size]="size"
      [weight]="weight"
      [disabled]="disabled"
    >
      <span itemprop="name"><ng-content></ng-content></span>
    </fl-link>
    <meta
      itemprop="position"
      [content]="position"
    />
  `,
  styleUrls: ['./breadcrumbs-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BreadcrumbsItemComponent {
  changeDetector = inject(ChangeDetectorRef);

  LinkColor = LinkColor;
  LinkHoverColor = LinkHoverColor;

  @Input() link: string;
  @Input() position: number;
  @Input() queryParams?: QueryParams;
  @Input() size = TextSize.BODY_12;
  @Input() weight = LinkWeight.BOLD;
  @Input() disabled = false;
  /** Marks the item as the current page, which mutes the other items */
  @Input() current = false;

  @HostBinding('attr.data-color')
  @Input()
  color = FontColor.FOREGROUND;

  @HostBinding('attr.data-text-transform')
  @Input()
  textTransform = TextTransform.UPPERCASE;

  @HostBinding('attr.data-mobile-show-breadcrumb')
  @Input()
  mobileShowBreadcrumb?: boolean;

  /** Set by the parent `fl-breadcrumbs` when another item is current */
  @HostBinding('attr.data-muted') muted = false;

  @HostBinding('attr.aria-current') get ariaCurrent(): 'page' | null {
    return this.current ? 'page' : null;
  }

  @HostBinding('attr.itemprop') itemprop = 'itemListElement';
  @HostBinding('attr.itemscope') itemscope = '';
  @HostBinding('attr.itemtype') itemtype = 'https://schema.org/ListItem';
  @HostBinding('attr.role') role = 'listitem';
}
